import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Lock, ShieldCheck, CreditCard, AlertCircle, RefreshCw, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { api } from '../../App';

// Publishable key resolution:
//   1. Build-time: `REACT_APP_STRIPE_PUBLISHABLE_KEY` baked into the bundle by CRA.
//   2. Runtime fallback: `GET /api/cards/config` reads `STRIPE_PUBLISHABLE_KEY` from backend env.
// The runtime path is critical for deployments where build-time env injection is unreliable.
// We memoise the resulting `loadStripe` promise per key so Stripe Elements don't reinitialise.
const BUILD_TIME_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
const _stripeCache = new Map();
function getStripePromise(pk) {
  if (!pk) return null;
  if (!_stripeCache.has(pk)) _stripeCache.set(pk, loadStripe(pk));
  return _stripeCache.get(pk);
}

// Pattern-match the handful of Stripe failure modes we want to rewrite into
// human-friendly copy, everything else falls through to the raw message.
const FRIENDLY_ERRORS = [
  {
    match: /(invalid api key|payments are temporarily unavailable|configuration error)/i,
    headline: 'Payments are temporarily unavailable',
    body: "We couldn't open the card form because of a setup issue on our side. Please try again in a moment. If this keeps happening, our support team can help finish your booking by hand.",
    retry: true,
  },
  {
    match: /(email|missing .*email)/i,
    headline: "We need your email first",
    body: 'Please add your email to the booking before saving a card, we use it to send the confirmation and any off-session receipts.',
    retry: false,
  },
  {
    match: /no longer available|already saved|already has a card/i,
    headline: 'A card is already on file',
    body: "This booking already has a saved card, you can proceed straight to payment.",
    retry: false,
  },
];

function friendlyError(raw) {
  if (!raw) return {
    headline: "We couldn't start the card form",
    body: 'Please try again in a moment, or contact support if this keeps happening.',
    retry: true,
  };
  for (const f of FRIENDLY_ERRORS) {
    if (f.match.test(raw)) return f;
  }
  return {
    headline: "We couldn't start the card form",
    body: raw,
    retry: true,
  };
}

function CardForm({ bookingId, maxCharge, onSuccess, onClose, guestToken }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  // Consent MUST default to false, the user must deliberately tick it.
  // This is both a UX requirement and the linchpin of the consent audit log
  // we use to defend any future chargeback over an off-session capture.
  const [consented, setConsented] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!consented) {
      toast.error('Please tick the authorisation checkbox to continue.');
      return;
    }
    setSubmitting(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(error.message || 'Card setup failed');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
      try {
        const endpoint = guestToken
          ? `/bookings/guest/${bookingId}/confirm-card`
          : `/bookings/${bookingId}/confirm-card`;
        // We intentionally send only { agreed: true }, everything else
        // (wording, policy version, max fee, IP, UA, timestamp) is recorded
        // server-side to keep the audit log tamper-proof.
        const consentPayload = { agreed: true };
        const body = guestToken
          ? { payment_method_id: setupIntent.payment_method, token: guestToken, card_on_file_consent: consentPayload }
          : { payment_method_id: setupIntent.payment_method, card_on_file_consent: consentPayload };
        const res = await api.post(endpoint, body);
        toast.success(`Card ending in ${res.data.card.last4} saved securely`);
        onSuccess?.(res.data.card);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not attach card to booking');
      }
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-[#FFF8E7] border border-[#D4AF37]/40 rounded-lg p-4 flex gap-3">
        <ShieldCheck className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
        <div className="text-[13px] text-[#1A1A1A] leading-relaxed">
          <p className="font-semibold mb-1">Your card will be securely stored</p>
          <p className="text-stone-600">
            It may be charged up to <strong className="text-[#1A1A1A]">£{maxCharge.toFixed(2)}</strong> if you cancel late or do not show up, according to the provider's policy. You will not be charged otherwise.
          </p>
        </div>
      </div>

      <div className="border border-stone-200 rounded-lg p-3 bg-white">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {/* Mandatory authorisation, server rejects the request if this isn't ticked */}
      <label
        className="flex gap-3 items-start p-3 rounded-lg border border-stone-200 bg-white cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
        data-testid="card-consent-row"
      >
        <Checkbox
          checked={consented}
          onCheckedChange={(v) => setConsented(Boolean(v))}
          className="mt-0.5"
          data-testid="card-consent-checkbox"
        />
        <span className="text-[12.5px] text-stone-600 leading-relaxed">
          I authorise Zuriya to securely store and charge this card for any applicable
          late-cancellation or no-show fees (up to <strong className="text-[#1A1A1A]">£{maxCharge.toFixed(2)}</strong>)
          in accordance with the booking&apos;s cancellation policy and the{' '}
          <Link to="/refunds" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">
            Refund Policy
          </Link>.
        </span>
      </label>

      <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
        <Lock className="h-3 w-3" />
        Encrypted &amp; processed by Stripe. Never stored on Zuriya servers.
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
          className="flex-1"
          data-testid="card-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || submitting || !consented}
          className="flex-[2] btn-primary gap-2"
          data-testid="card-confirm-btn"
        >
          <CreditCard className="h-4 w-4" />
          {submitting ? 'Saving...' : 'Save card & confirm booking'}
        </Button>
      </div>
    </form>
  );
}

function SetupErrorPanel({ error, maxCharge, canRetry, onRetry, onClose }) {
  const { headline, body, retry } = friendlyError(error);
  const [showDetails, setShowDetails] = useState(false);
  const showRetry = retry && canRetry;
  return (
    <div className="space-y-4" data-testid="card-setup-error">
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-red-800">{headline}</p>
            <p className="text-[13px] text-red-700 leading-relaxed">{body}</p>
          </div>
        </div>
        {error && error !== body && (
          <div className="mt-3 pl-8">
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              className="text-xs text-red-700/70 underline underline-offset-2 hover:text-red-800"
              data-testid="card-setup-error-toggle-details"
            >
              {showDetails ? 'Hide technical details' : 'Show technical details'}
            </button>
            {showDetails && (
              <pre className="mt-2 whitespace-pre-wrap break-words text-[11px] text-red-900/80 bg-red-100/60 border border-red-200 rounded p-2 font-mono">
                {error}
              </pre>
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-4 text-[12.5px] text-stone-600 leading-relaxed">
        <p className="font-medium text-[#1A1A1A] mb-1">Why do we need a card on file?</p>
        <p>
          This provider's cancellation policy allows them to charge up to{' '}
          <strong className="text-[#1A1A1A]">£{Number(maxCharge || 0).toFixed(2)}</strong>{' '}
          for late cancellations or no-shows. Your card isn't charged now, only if the policy kicks in.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
          data-testid="card-setup-error-close"
        >
          Close
        </Button>
        {showRetry ? (
          <Button
            type="button"
            onClick={onRetry}
            className="flex-[2] btn-primary gap-2"
            data-testid="card-setup-retry"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        ) : (
          <Button
            asChild
            className="flex-[2] btn-primary gap-2"
            data-testid="card-setup-contact-support"
          >
            <a href="/contact">
              <LifeBuoy className="h-4 w-4" />
              Contact support
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

export function CardOnFileDialog({
  open, onOpenChange, bookingId, maxCharge = 0, onSuccess, guestToken,
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [publishableKey, setPublishableKey] = useState(BUILD_TIME_PK);

  // Resolve the publishable key. Prefer build-time (already set). Fall back to
  // runtime `/api/cards/config` if the bundle wasn't built with the env var.
  useEffect(() => {
    if (!open || publishableKey) return;
    let cancelled = false;
    api.get('/cards/config')
      .then(res => {
        const pk = res.data?.publishable_key;
        if (!cancelled && pk) setPublishableKey(pk);
        else if (!cancelled) setError('Stripe is not configured for this site. Please contact support.');
      })
      .catch(() => {
        if (!cancelled) setError('Could not reach Stripe configuration, please retry.');
      });
    return () => { cancelled = true; };
  }, [open, publishableKey]);

  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  const startSetup = useCallback(() => {
    if (!bookingId) return;
    setClientSecret(null);
    setError(null);
    const endpoint = guestToken
      ? `/bookings/guest/${bookingId}/setup-card`
      : `/bookings/${bookingId}/setup-card`;
    const body = guestToken ? { token: guestToken } : {};
    api.post(endpoint, body)
      .then(res => setClientSecret(res.data.client_secret))
      .catch(err => setError(err.response?.data?.detail || 'Could not start card setup'));
  }, [bookingId, guestToken]);

  useEffect(() => {
    if (!open || !publishableKey) return;
    startSetup();
  }, [open, attempt, publishableKey, startSetup]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
            Save your card
          </DialogTitle>
          <DialogDescription className="sr-only">
            Save a card on file to confirm your booking. Your card is stored securely with Stripe and only charged according to the provider's cancellation policy.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <SetupErrorPanel
            error={error}
            maxCharge={maxCharge}
            canRetry
            onRetry={() => setAttempt((n) => n + 1)}
            onClose={() => onOpenChange(false)}
          />
        )}

        {!clientSecret && !error && (
          <div className="py-10 text-center text-sm text-stone-400" data-testid="card-setup-loading">
            {publishableKey ? 'Loading secure form...' : 'Connecting to Stripe...'}
          </div>
        )}

        {clientSecret && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#D4AF37',
                  colorBackground: '#ffffff',
                  colorText: '#1A1A1A',
                  borderRadius: '8px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                },
              },
            }}
          >
            <CardForm
              bookingId={bookingId}
              maxCharge={maxCharge}
              guestToken={guestToken}
              onSuccess={(card) => {
                onSuccess?.(card);
                onOpenChange(false);
              }}
              onClose={() => onOpenChange(false)}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

